import { lazy, Suspense, useMemo } from "react";
import { Link } from "@/lib/router-compat";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight, BadgeDollarSign, CalendarCheck, TrendingDown, Caravan } from "lucide-react";
import { useInventoryHome } from "@/hooks/useInventory";
import RVCard from "@/components/inventory/RVCard";
import PaymentEstimator from "./PaymentEstimator";
import PreviewBadge from "./PreviewBadge";
import CarmaxFooter from "./CarmaxFooter";
import CarmaxHeader from "./CarmaxHeader";
import CarmaxPageHero from "./CarmaxPageHero";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useCarMaxImages } from "@/hooks/useCarMaxImages";
import { homepageCdnImage } from "@/lib/homepageImages";
import LazySection from "@/components/ui/LazySection";
import { useInteractionReady } from "@/components/ui/InteractionGate";
import heroImg from "@/assets/carmax/hero-family-rv.jpg";
import tradeImg from "@/assets/carmax/trade-in.webp";
import travelTrailer from "@/assets/rv-types/travel-trailer.webp";
import liteTrailer from "@/assets/rv-types/lite-trailer.webp";
import destination from "@/assets/rv-types/destination-trailer.webp";
import fifthWheel from "@/assets/rv-types/fifth-wheel.webp";
import popup from "@/assets/rv-types/folding-popup.webp";
import toyHauler from "@/assets/rv-types/toy-hauler.webp";
import classA from "@/assets/rv-types/class-a.webp";
import classB from "@/assets/rv-types/class-b.webp";
import classC from "@/assets/rv-types/class-c.webp";
import specials from "@/assets/rv-types/specials.webp";

const DealOfTheWeek = lazy(() => import("@/components/home/DealOfTheWeek"));

const ACTIVE_STATUSES = ["available", "pending", "coming_soon"];

const CHIPS = [
  { label: "Shop all", to: "/rv-search" },
  { label: "New", to: "/rv-search?condition=new" },
  { label: "Used", to: "/rv-search?condition=used" },
  { label: "Under $30K", to: "/rv-search?price_max=30000" },
  { label: "Under $299/mo", to: "/rv-search?payment_max=299" },
  { label: "Sleeps 6+", to: "/rv-search?sleeps_min=6" },
  { label: "Bunkhouse", to: "/rv-search?feature=bunkhouse" },
  { label: "Easy tow", to: "/rv-search?weight_max=5500" },
  { label: "Specials", to: "/rv-search?special=manager" },
  { label: "Parts", to: "/parts-catalog" },
];

/**
 * A CMS-saved picture is served from the CDN. If that address ever fails to
 * load, render the packaged copy instead so the page never shows a broken
 * image. Guarded by a flag so a second failure can't loop.
 */
const fallbackTo = (src: string) => (e: React.SyntheticEvent<HTMLImageElement>) => {
  const el = e.currentTarget;
  if (el.getAttribute("data-fell-back")) return;
  el.setAttribute("data-fell-back", "1");
  el.src = src;
};

const TYPES: { label: string; key: string; to: string; match: string[]; img: string; special?: boolean }[] = [
  { label: "Travel Trailers", key: "travel-trailer", to: "/rv-search?type=travel-trailer", match: ["Travel Trailer", "TRAV"], img: travelTrailer },
  { label: "Lite Trailers", key: "lite-trailer", to: "/rv-search?type=lite-travel-trailer", match: ["Lite Travel Trailer", "Ultra-Lite Trailer"], img: liteTrailer },
  { label: "Fifth Wheels", key: "fifth-wheel", to: "/rv-search?type=fifth-wheel", match: ["Fifth Wheel"], img: fifthWheel },
  { label: "Toy Haulers", key: "toy-hauler", to: "/rv-search?types=Toy%20Hauler%20Travel%20Trailer,Toy%20Hauler%20Fifth%20Wheel", match: ["Toy Hauler Travel Trailer", "Toy Hauler Fifth Wheel"], img: toyHauler },
  { label: "Destination", key: "destination", to: "/rv-search?type=destination-trailer", match: ["Destination Trailer"], img: destination },
  { label: "Pop-Ups", key: "popups", to: "/rv-search?type=folding-pop-up-camper", match: ["Folding Pop-Up Camper"], img: popup },
  { label: "Class A", key: "class-a", to: "/rv-search?type=motor-home-class-a", match: ["Motor Home Class A", "Motor Home Class A - Diesel"], img: classA },
  { label: "Class B", key: "class-b", to: "/rv-search?type=motor-home-class-b", match: ["Motor Home Class B", "Motor Home Class B - Diesel"], img: classB },
  { label: "Class C", key: "class-c", to: "/rv-search?type=motor-home-class-c", match: ["Motor Home Class C", "Motor Home Class C - Diesel"], img: classC },
  { label: "Specials", key: "specials", to: "/rv-search?special=true", match: [], img: specials, special: true },
];

export default function CarmaxHome() {
  // CMS-saved picture addresses (CDN-hosted, editable in the page editor).
  // The packaged imports above stay in the bundle as the fallback.
  const { data: cmsImages } = useCarMaxImages();
  const engaged = useInteractionReady();
  const { data: home } = useInventoryHome(engaged);
  const available = useMemo(() => home?.available ?? [], [home]);
  const typeCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of TYPES) m[t.label] = (home?.counts ?? []).filter((u) => ACTIVE_STATUSES.includes(u.status) && u.rv_type != null && t.match.includes(u.rv_type)).length;
    return m;
  }, [home]);
  const under30 = (home?.counts ?? []).filter((u) => u.status === "available" && Number(u.price) > 0 && Number(u.price) < 30000).length;


  return (
    <Layout header={<CarmaxHeader homepage />} footer={<CarmaxFooter homepage />}>
      <div className="bg-background">

        {/* Hero — rotates through the Home Page slides saved in the page editor */}
        <CarmaxPageHero
          cmsSlug="home"
          slides={[{
            image: heroImg,
            title: "Enjoy Life's Journey!",
            subtitle: "Family owned & operated RV dealer in Castle Hayne, NC since 1997. New & used motorhomes, travel trailers, service, parts, and rentals.",
            cta: <Button asChild size="lg" className="rounded-full"><Link to="/rv-search">Find your RV</Link></Button>,
          }]}
        />

        {/* Sell + Tow Guide cards */}
        <section className="container mt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col items-start justify-between gap-3 rounded-2xl border bg-card p-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3">
                <BadgeDollarSign className="h-8 w-8 shrink-0 text-primary" />
                <div className="font-heading text-lg font-semibold leading-tight text-foreground">Wanna sell your RV?</div>
              </div>
              <Button asChild variant="outline" className="rounded-full"><Link to="/sell-your-rv">Get my offer <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
            </div>
            <div className="flex flex-col items-start justify-between gap-3 rounded-2xl border bg-card p-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3">
                <Caravan className="h-8 w-8 shrink-0 text-primary" />
                <div className="font-heading text-lg font-semibold leading-tight text-foreground">What can your truck tow?</div>
              </div>
              <Button asChild variant="outline" className="rounded-full"><Link to="/tow-guides">Towing guide <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 pb-2" role="group" aria-label="Quick RV filters">
            {CHIPS.map((c) => (
              <Button key={c.label} asChild variant="outline" className="h-auto min-h-11 shrink-0 rounded-full border-border bg-card px-4 py-2 font-semibold text-secondary hover:border-primary hover:bg-card hover:text-primary md:min-h-0">
                <Link to={c.to}>{c.label}</Link>
              </Button>
            ))}
          </div>
        </section>

        {/* Body styles */}
        <LazySection as="section" className="container pt-10" fallback={<div className="min-h-[380px] md:min-h-[420px]" />}>
          <h2 className="mb-4 font-heading text-2xl font-bold text-foreground">Shop popular RV types</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
            {TYPES.map((t) => (
              <Link key={t.label} to={t.to} className="group overflow-hidden rounded-xl border bg-card text-center transition hover:border-primary hover:shadow-md">
                <img src={homepageCdnImage(cmsImages?.types?.[t.key] || t.img)} onError={fallbackTo(t.img)} alt={t.label} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <div className="px-2 py-2">
                  <div className="text-sm font-semibold text-foreground">{t.label}</div>
                  {t.special ? (
                    <div className="text-xs font-bold uppercase tracking-wide text-primary">Save</div>
                  ) : (
                    <div className="text-xs text-muted-foreground">{typeCounts[t.label] ?? 0} available</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </LazySection>

        {/* Ready today */}
        <LazySection as="section" className="container pt-10" fallback={<div className="min-h-[480px]" />}>
          <div className="mb-4 flex items-end justify-between">
            <h2 className="font-heading text-2xl font-bold text-foreground">Ready today near <span className="text-primary underline">Castle Hayne</span></h2>
            <Link to="/rv-search" className="rounded-full border px-3 py-1 text-sm font-medium hover:border-primary hover:text-primary">Show more</Link>
          </div>
          {available.length > 0 ? (
            <Carousel opts={{ align: "start" }}>
              <CarouselContent>
                {available.map((rv, i) => (
                  <CarouselItem key={rv.id} className="basis-[80%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <RVCard rv={rv} priority={i < 2} noCycle />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex" />
              <CarouselNext className="hidden md:flex" />
            </Carousel>
          ) : <div className="h-80 animate-pulse rounded-xl bg-muted" />}
        </LazySection>

        {/* Budget */}
        <LazySection as="section" className="container pt-10" fallback={<div className="min-h-[450px]" />}>
          <div className="grid gap-6 rounded-2xl border bg-card p-5 md:grid-cols-[1.6fr_1fr] md:p-6">
            <div>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">Get pre-qualified</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground">Find RVs that fit your life and budget</h2>
              <div className="mt-4"><PaymentEstimator compact={false} /></div>
            </div>
            <div className="flex flex-col justify-center rounded-xl bg-muted/50 p-6 text-center">
              <div className="text-sm text-muted-foreground">Shop by monthly payment</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[199, 299, 399, 499].map((n) => (
                  <Button key={n} asChild variant="outline"><Link to={`/rv-search?payment_max=${n}`}>Under ${n}/mo</Link></Button>
                ))}
              </div>
              <Button asChild size="lg" className="mt-4 rounded-full"><Link to="/rv-financing">Get pre-qualified</Link></Button>
              <p className="mt-2 text-xs text-muted-foreground">No impact to your credit score to start</p>
            </div>
          </div>
        </LazySection>

        {/* Find your ride */}
        <LazySection as="section" className="container pt-10" fallback={<div className="min-h-[260px] md:min-h-[130px]" />}>
          <h2 className="mb-4 font-heading text-2xl font-bold text-foreground">Let's find your RV</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              { i: BadgeDollarSign, t: "Under $30,000", d: `${under30} matches`, to: "/rv-search?price_max=30000" },
              { i: CalendarCheck, t: "Schedule a visit", d: "Walk through any unit", to: "/contact-us" },
              { i: TrendingDown, t: "Specials & price drops", d: "Manager deals this week", to: "/rv-search?special=manager" },
            ].map(({ i: I, t, d, to }) => (
              <Link key={t} to={to} className="flex items-center gap-4 rounded-xl border bg-card p-4 transition hover:border-primary hover:shadow-md">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary/10"><I className="h-6 w-6 text-primary" /></span>
                <div className="min-w-0 flex-1"><div className="font-semibold text-foreground">{t}</div><div className="text-sm text-muted-foreground">{d}</div></div>
                <ArrowRight className="h-4 w-4 shrink-0 text-primary" />
              </Link>
            ))}
          </div>
        </LazySection>

        {/* Trade-in */}
        <LazySection as="section" className="container pt-10" fallback={<div className="h-[420px]" />}>
          <div className="relative overflow-hidden rounded-2xl">
            <img src={homepageCdnImage(cmsImages?.trade_in || tradeImg)} onError={fallbackTo(tradeImg)} alt="Customer checking his RV trade-in offer on his phone" loading="lazy" width={1280} height={564} className="h-[420px] w-full object-cover object-right" />
            <div className="absolute inset-y-0 left-0 flex items-center p-5 md:p-10">
              <div className="w-full max-w-sm rounded-xl bg-card p-5 shadow-xl">
                <div className="text-sm font-semibold text-muted-foreground">Sell/trade-in</div>
                <h2 className="font-heading text-3xl font-bold text-foreground">Real offers, really fast</h2>
                <p className="mt-2 text-sm text-muted-foreground">Tell us about your RV and our team will get you a value — no obligation.</p>
                <Button asChild size="lg" className="mt-4 w-full rounded-full"><Link to="/contact-us-trade-ins">Get my offer <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
              </div>
            </div>
          </div>
        </LazySection>

        <LazySection className="pt-10" fallback={<div className="min-h-[553px]" />}><Suspense fallback={<div className="min-h-[553px]" />}><DealOfTheWeek /></Suspense></LazySection>

        {/* Info */}
        <LazySection as="section" className="container py-12" fallback={<div className="min-h-[400px] md:min-h-[220px]" />}>
          <h2 className="mb-4 font-heading text-2xl font-bold text-foreground">More from Rex &amp; Sons</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              { t: "RV Service", d: "Certified techs for repairs, warranty and upgrades.", to: "/rv-service", c: "Schedule service" },
              { t: "RV Parts", d: "100,000+ parts shipped fast or picked up.", to: "/parts-catalog", c: "Shop parts" },
              { t: "Rentals", d: "Try the RV life before you buy.", to: "/rentals", c: "See rentals" },
            ].map((x) => (
              <Link key={x.t} to={x.to} className="rounded-xl border bg-card p-5 transition hover:border-primary hover:shadow-md">
                <div className="font-heading text-lg font-semibold text-foreground">{x.t}</div>
                <p className="mt-1 text-sm text-muted-foreground">{x.d}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">{x.c} <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </LazySection>
      </div>
      <PreviewBadge livePath="/" />
    </Layout>
  );
}
